/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend/src/modules/messaging/`
 * (read-only source — never edit that checkout from here):
 *   - templates.controller.ts, campaigns.controller.ts,
 *     messaging-settings.controller.ts, unsubscribe.controller.ts
 *   - campaigns.service.ts, campaign-send.service.ts, campaign-channel.ts,
 *     messaging-settings.service.ts, quiet-hours.ts
 *   - dto/template.dto.ts, campaign.dto.ts, messaging-settings.dto.ts
 *   - prisma/schema.prisma (MessageTemplate, Campaign, CampaignSend,
 *     TenantMessagingSettings and their enums)
 * plus `docs/ADR/ADR-0026-messaging-and-campaigns.md`. One file, so a later
 * drift on that side is a one-file fix here.
 *
 * Three decisions this UI has to honour, not merely display:
 *
 * 1. **Consent is per-channel and never implied by a contact detail.**
 *    That model lives in `customers/crm/consent.service.ts` — a different
 *    module (ADR-0010), already surfaced by `CRM/customer/ConsentTab.tsx`
 *    and `CRM/segments/SegmentMembersDialog.tsx`. This module reuses those
 *    types (`ContactChannel`, `SegmentContactableResult`) from
 *    `@/Pages/CRM/lib` rather than redeclaring them, and `CampaignsTab`
 *    calls the same `GET /segments/:id/contactable` endpoint that dialog
 *    does before offering to activate a campaign — a segment is never
 *    presented as reach without checking who on it actually is one.
 *
 * 2. **Quiet hours are timezone-aware, branch-first then tenant** —
 *    `MessagingSettingsService.resolveTimezone` reads `Branch.timezone`
 *    when a branch is in play and `Tenant.timezone` otherwise. In practice
 *    today that fallback never triggers the branch half: a `Campaign`
 *    carries no `branchId` (ADR-0026 — campaigns are tenant-wide), so every
 *    quiet-hours check currently resolves through the TENANT branch of that
 *    method. `GET /messaging/settings` is itself tenant-wide (no `branchId`
 *    param exists on it), and — the sharper gap — **nothing gated on
 *    `settings.view`/`campaigns.view` can read `Tenant.timezone` at all**:
 *    `TenantsController` only exposes `GET /tenants/:id` behind
 *    `platform.manage_tenants`. `SettingsTab` says this plainly rather than
 *    fabricating a zone: it lists every branch's own timezone (from
 *    `GET /branches`, which a tenant user CAN read) as the best available
 *    proxy, and states that quiet hours apply tenant-wide on a zone this
 *    screen cannot itself confirm.
 *
 * 3. **WhatsApp and PUSH are deliberately unimplemented.** `MESSAGING_CHANNELS`
 *    (`campaign-channel.ts`) is `[EMAIL, SMS]` — the only two a template or
 *    campaign may target; WhatsApp is not even a `NotificationChannel`
 *    member (it exists only as a `ContactChannel` consent can be recorded
 *    against, for a channel with no transport yet). `PUSH` **is** a
 *    `NotificationChannel` but every send through it hits
 *    `UnimplementedChannel`, which parks the row `PENDING` with an honest
 *    `"{channel} adapter not implemented"` error rather than lying about
 *    delivery. `TemplatesTab`/`CampaignsTab` offer only EMAIL and SMS as
 *    channels to create against — never WhatsApp, never PUSH.
 */
import { apiError, apiStatus } from '../Workforce/lib';

export { apiError, apiStatus };

/** The only two channels a template or campaign may target — `MESSAGING_CHANNELS` on the backend. */
export const MessagingChannel = { EMAIL: 'EMAIL', SMS: 'SMS' } as const;
export type MessagingChannel = (typeof MessagingChannel)[keyof typeof MessagingChannel];

export const CampaignStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  SENDING: 'SENDING',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
} as const;
export type CampaignStatus = (typeof CampaignStatus)[keyof typeof CampaignStatus];

export const CampaignTriggerType = {
  ONE_OFF: 'ONE_OFF',
  WELCOME: 'WELCOME',
  WIN_BACK: 'WIN_BACK',
  POST_VISIT: 'POST_VISIT',
  BIRTHDAY: 'BIRTHDAY',
  LOYALTY_MILESTONE: 'LOYALTY_MILESTONE',
} as const;
export type CampaignTriggerType = (typeof CampaignTriggerType)[keyof typeof CampaignTriggerType];

export const CampaignSendStatus = {
  SENT: 'SENT',
  SKIPPED_NO_CONSENT: 'SKIPPED_NO_CONSENT',
  SKIPPED_FATIGUE_CAP: 'SKIPPED_FATIGUE_CAP',
  SKIPPED_INVALID_RECIPIENT: 'SKIPPED_INVALID_RECIPIENT',
  FAILED: 'FAILED',
} as const;
export type CampaignSendStatus = (typeof CampaignSendStatus)[keyof typeof CampaignSendStatus];

// -------------------------------------------------------------- templates

/** `key` groups a template's locale variants — same key and channel, one row per locale. */
export interface MessageTemplate {
  id: string;
  key: string;
  channel: MessagingChannel;
  locale: string;
  /** EMAIL only; SMS ignores this. */
  subject: string | null;
  /** Source with `{{placeholders}}` — never pre-rendered output. */
  body: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMessageTemplateInput {
  key: string;
  channel: MessagingChannel;
  locale?: string;
  subject?: string;
  body: string;
  variables?: string[];
  isActive?: boolean;
}

export type UpdateMessageTemplateInput = Partial<
  Pick<CreateMessageTemplateInput, 'subject' | 'body' | 'variables' | 'isActive'>
>;

export interface TemplatePreview {
  subject: string | null;
  body: string;
}

// -------------------------------------------------------------- campaigns

/**
 * Every variable a CAMPAIGN template may reference — `activate()` refuses
 * any other placeholder, because these five are all a campaign send can
 * actually supply. A template meant for a one-off transactional notify (not
 * a campaign) may use others; a campaign-backing template may not.
 */
export const RESERVED_CAMPAIGN_VARIABLES = [
  'customerName',
  'customerFirstName',
  'customerEmail',
  'customerPhone',
  'unsubscribeUrl',
] as const;

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel: MessagingChannel;
  templateId: string;
  triggerType: CampaignTriggerType;
  audienceSegmentId: string;
  repeatable: boolean;
  status: CampaignStatus;
  scheduledFor: string | null;
  activatedAt: string | null;
  nextEvalAt: string | null;
  triggerConfig: Record<string, unknown> | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignInput {
  name: string;
  description?: string;
  channel: MessagingChannel;
  templateId: string;
  triggerType: CampaignTriggerType;
  audienceSegmentId: string;
  repeatable?: boolean;
  scheduledFor?: string;
  triggerConfig?: Record<string, unknown>;
}

export type UpdateCampaignInput = Partial<
  Pick<CreateCampaignInput, 'name' | 'description' | 'repeatable' | 'scheduledFor' | 'triggerConfig'>
>;

export interface CampaignSend {
  id: string;
  campaignId: string;
  customerId: string;
  status: CampaignSendStatus;
  notificationId: string | null;
  cycleKey: string;
  createdAt: string;
}

// --------------------------------------------------------------- settings

/** `GET/PATCH /messaging/settings` — tenant-wide; there is no branch-scoped equivalent yet. See this file's header note 2. */
export interface MessagingSettings {
  tenantId: string;
  quietHoursEnabled: boolean;
  /** Local hour 0-23, inclusive start. */
  quietHoursStart: number;
  /** Local hour 0-23, exclusive end. */
  quietHoursEnd: number;
  /** Marketing sends to one customer per trailing 7 days. */
  maxMarketingMessagesPerWeek: number;
}

export type UpdateMessagingSettingsInput = Partial<
  Omit<MessagingSettings, 'tenantId'>
>;

export function fmtHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}
